import React, { useEffect, useState } from "react";
import { api, fmtDate } from "../lib/api";
import { Users, Shield, ShieldOff, Trash2, BarChart3, Mail, Activity, Crown } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.get("/admin/users"), api.get("/admin/stats")]);
      setUsers(u.data);
      setStats(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleAdmin = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { is_admin: !u.is_admin });
      toast.success(`${u.is_admin ? "Demoted" : "Promoted"} ${u.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cascades:\n- ${u.holdings_count} holdings\n- All dividends, preferences, sessions\n\nIRREVERSIBLE.`)) return;
    try {
      const { data } = await api.delete(`/admin/users/${u.user_id}`);
      toast.success(`Deleted ${u.email} (${data.deleted_holdings} holdings, ${data.deleted_dividends} dividends)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const grantTier = async (u) => {
    const choice = window.prompt(
      `Grant tier to ${u.email}\n\nCurrent tier: ${u.tier}\n\nType one:\n  free  — revoke subscription\n  pro   — grant Pro for 365 days\n  founder — grant Founder Lifetime (10 years)`,
      "pro"
    );
    if (!choice) return;
    const normalized = choice.trim().toLowerCase();
    const tier = normalized.startsWith("founder") ? "founder_lifetime"
               : normalized === "pro" ? "pro"
               : normalized === "free" ? "free"
               : null;
    if (!tier) {
      toast.error("Type free, pro, or founder");
      return;
    }
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/grant-tier`, { tier });
      toast.success(`Granted ${data.tier} to ${u.email}${data.current_period_end ? ` (until ${fmtDate(data.current_period_end)})` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    }
  };

  const filtered = users.filter((u) =>
    !filter || u.email.toLowerCase().includes(filter.toLowerCase()) || (u.name || "").toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone bg-white px-8 py-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold mb-1">// ADMIN · USERS</div>
        <h1 className="text-2xl font-bold text-graphite tracking-tight">User management</h1>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-stone border-b border-stone bg-white">
          <StatTile icon={Users} label="Total Users" value={stats.users.total} sub={`${stats.users.admins} admins`} />
          <StatTile icon={Shield} label="Pro" value={stats.subscriptions.pro} sub={`${stats.subscriptions.founder_lifetime} founders`} accent="paars" />
          <StatTile icon={Activity} label="Holdings" value={stats.holdings.total} sub={`avg ${stats.holdings.avg_per_user}/user`} />
          <StatTile icon={Mail} label="Emails Sent" value={stats.emails_sent} sub={`${stats.dividends_logged} divs logged`} />
          <StatTile icon={BarChart3} label="Catalog" value={stats.catalog_size} sub={stats.top_tickers[0] ? `top: ${stats.top_tickers[0].ticker}` : "—"} />
        </div>
      )}

      <div className="p-8">
        <div className="flex items-center justify-between mb-4">
          <input
            data-testid="user-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by email or name..."
            className="w-72 bg-white border border-stone px-3 py-2 text-sm focus:border-paars focus:outline-none"
          />
          <div className="text-xs text-graphite/55 font-mono">{filtered.length} of {users.length}</div>
        </div>

        <div className="border border-stone bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone/50 border-b border-stone">
                {["User", "Tier", "Holdings", "Role", "Joined", "Actions"].map((h) => (
                  <th key={h} className={`px-3 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-graphite/55 ${h === "Holdings" ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={6} className="p-6 text-center text-graphite/45 font-mono text-xs">LOADING...</td></tr>)}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="p-12 text-center text-graphite/55 text-sm">No users match.</td></tr>
              )}
              {filtered.map((u) => (
                <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-stone/50 hover:bg-stone/30">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      {u.picture && <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />}
                      <div className="min-w-0">
                        <div className="font-semibold text-graphite text-sm truncate">{u.name || "—"}</div>
                        <div className="text-xs text-graphite/55 font-mono truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-[10px] tracking-widest uppercase font-bold px-2 py-1 ${u.tier === "founder_lifetime" ? "bg-gold text-graphite" : u.tier === "pro" ? "bg-paars text-graphite" : "bg-stone text-graphite/65"}`}>
                      {u.tier}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-graphite/75">{u.holdings_count}</td>
                  <td className="px-3 py-3">
                    {u.is_admin ? (
                      <span className="text-[10px] tracking-widest uppercase font-bold text-paars-dark">ADMIN</span>
                    ) : (
                      <span className="text-[10px] tracking-widest uppercase font-bold text-graphite/45">USER</span>
                    )}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-graphite/55">{fmtDate(u.created_at)}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-2">
                      <button
                        data-testid={`grant-tier-${u.user_id}`}
                        onClick={() => grantTier(u)}
                        title="Grant tier (free / pro / founder)"
                        className="text-graphite/55 hover:text-gold"
                      >
                        <Crown className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`toggle-admin-${u.user_id}`}
                        onClick={() => toggleAdmin(u)}
                        title={u.is_admin ? "Demote" : "Promote to admin"}
                        className="text-graphite/55 hover:text-paars-dark"
                      >
                        {u.is_admin ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                      </button>
                      <button
                        data-testid={`delete-user-${u.user_id}`}
                        onClick={() => del(u)}
                        title="Delete user (cascade)"
                        className="text-graphite/55 hover:text-burgundy"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Top tickers */}
        {stats && stats.top_tickers.length > 0 && (
          <div className="mt-8 border border-stone bg-white">
            <div className="px-4 py-3 border-b border-stone">
              <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// MOST TRACKED BONDS</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-stone/50">
              {stats.top_tickers.map((t) => (
                <div key={t.ticker} className="bg-white p-4">
                  <div className="font-mono font-bold text-graphite text-sm tracking-wider">{t.ticker}</div>
                  <div className="text-xs text-graphite/55 font-mono">{t.tracked_by} users</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${accent === "paars" ? "text-paars-dark" : "text-graphite/55"}`} />
        <div className="text-[10px] tracking-[0.3em] uppercase text-graphite/55 font-bold">{label}</div>
      </div>
      <div className="text-2xl font-bold text-graphite font-mono">{value}</div>
      {sub && <div className="text-[11px] text-graphite/55 font-mono mt-0.5">{sub}</div>}
    </div>
  );
}
